import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getHostelAdmissionsPortal, getHostelAdmissionPortal, addHostelAdmissionPortal,
  submitHostelRequest,
  updateHostelAdmissionPortal, deleteHostelAdmissionPortal,
  checkoutHostelAdmissionPortal,
  getHostelAdmissionOptionsPortal,
  getAttendanceHistoryPortal, getMealsHistoryPortal, getHostelPaymentsPortal, getInvoicesPortal, getInvoicePortal,
  getBillingAdjustmentsPortal, lookupByCnicPortal,
} from '../../api';
import { FiActivity, FiBarChart2, FiCalendar, FiCheckSquare, FiCoffee, FiCreditCard, FiDollarSign, FiEdit2, FiFileText, FiHome, FiLogOut, FiPhone, FiPlus, FiPrinter, FiSlash, FiTrash2, FiUpload, FiUser, FiX } from 'react-icons/fi';
import ResidentProfileModal from '../ResidentProfileModal';

const MAX_DOB = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 10); return d.toISOString().split('T')[0]; })();

function validateAdmission(form) {
  const name = (form.full_name || '').trim();
  if (!name || name.length < 2) return 'Full name is required (letters only, min 2 characters)';
  if (!/^[a-zA-Z\s'.-]+$/.test(name)) return 'Full name must contain letters only';
  const cnic = (form.cnic || '').replace(/-/g, '');
  if (cnic && !/^\d{13}$/.test(cnic)) return 'CNIC must be exactly 13 digits (e.g. 3520112345671)';
  const phone = (form.contact_no || '').replace(/[\s-]/g, '');
  if (phone && !/^03\d{9}$/.test(phone)) return 'Contact must be Pakistani format: 03XXXXXXXXX (11 digits)';
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Invalid email address';
  if (form.dob && form.dob > MAX_DOB) return 'Resident must be at least 10 years old';
  if (!form.campus_id || !form.course_id || !form.course_level) return 'Campus, course, and course level are required';
  if (!form.date_of_admission) return 'Date of admission is required';
  if (!form.monthly_rent || Number(form.monthly_rent) <= 0) return 'Monthly rent must be a positive number';
  return null;
}

const emptyForm = {
  full_name: '', father_name: '', gender: '', dob: '',
  cnic: '', dept_batch_program: '', campus_id: '', course_id: '', course_level: '', contact_no: '', email: '', permanent_address: '',
  guardian_name: '', guardian_relationship: '', guardian_contact: '',
  date_of_admission: '', monthly_rent: '', student_signature: '', incharge_signature: '', notes: ''
};

// Signature Pad (same clean component)
function SignaturePad({ label, value, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [mode, setMode] = useState('draw');

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onStart = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#f5bc1f'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    setDrawing(true);
    const pos = getPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(pos.x, pos.y);
  };

  const onMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y); ctx.stroke();
  };

  const onEnd = () => { setDrawing(false); if (canvasRef.current) onChange(canvasRef.current.toDataURL()); };
  const clearCanvas = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };
  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { onChange(ev.target.result); setMode('preview'); };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '10px', background: 'rgba(255,255,255,0.03)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '4px' }}>
        <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>{label}</span>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button type="button" onClick={() => setMode('draw')} style={{ padding: '3px 8px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: '#94a3b8', fontSize: '11px', cursor: 'pointer' }}><FiEdit2 /></button>
          <label style={{ padding: '3px 8px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: '#94a3b8', fontSize: '11px', cursor: 'pointer', margin: 0 }}>
            <FiUpload /> <input type="file" accept="image/*" hidden onChange={handleUpload} />
          </label>
          {value && <button type="button" onClick={() => { clearCanvas(); onChange(''); setMode('draw'); }} style={{ padding: '3px 8px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '4px', color: '#f87171', fontSize: '11px', cursor: 'pointer' }}>×</button>}
        </div>
      </div>
      {mode === 'preview' && value ? (
        <img src={value} alt="sig" style={{ maxHeight: '70px', maxWidth: '100%', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '4px', cursor: 'pointer' }} onClick={() => setMode('draw')} />
      ) : (
        <canvas ref={canvasRef} width={260} height={70}
          style={{ border: '1px dashed rgba(255,255,255,0.15)', borderRadius: '4px', background: 'rgba(0,0,0,0.4)', display: 'block', touchAction: 'none', cursor: 'crosshair' }}
          onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
          onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
        />
      )}
    </div>
  );
}

// Format ISO date strings from MySQL
const fmtDate = v => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-PK', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

// Print View (hostel portal version)
function PrintView({ record, hostelName, onClose }) {
  if (!record) return null;

  const handlePrint = () => {
    const el = document.getElementById('hostel-print-area');
    if (!el) return;
    const clone = el.cloneNode(true);
    const inlineStyle = clone.querySelector('style');
    if (inlineStyle) inlineStyle.remove();
    const w = window.open('', '_blank');
    w.document.write('<!DOCTYPE html><html><head><title>Admission Form</title><style>body{margin:20px;font-family:Arial,sans-serif;}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important;}</style></head><body>');
    w.document.write(clone.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); w.close(); }, 400);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#1a1a1a', borderRadius: '12px', width: '100%', maxWidth: '700px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #333', background: '#111' }}>
          <h3 style={{ color: '#fff', margin: 0 }}>Admission Form</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handlePrint} style={{ padding: '6px 14px', background: '#0e1e45', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '13px' }}><FiPrinter /> Print</button>
            <button onClick={onClose} style={{ padding: '6px 14px', background: '#374151', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '13px' }}><FiX /></button>
          </div>
        </div>
        <div style={{ padding: '20px' }} id="hostel-print-area">
          <style>{`@media print { body * { visibility: hidden; } #hostel-print-area, #hostel-print-area * { visibility: visible; } #hostel-print-area { position: fixed; left: 0; top: 0; width: 100%; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; } }`}</style>
          <div style={{ border: '2px solid #1d4ed8', borderRadius: '8px', overflow: 'hidden', background: '#fff', color: '#111' }}>
            {/* Dark Navy Header */}
            <div style={{ background: '#0c2340', padding: '20px 24px 16px', textAlign: 'center', borderBottom: '3px solid #1d4ed8' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', marginBottom: '8px' }}>
                <img src="/assets/hit-logo.png" alt="HIT" style={{ height: '54px', width: 'auto', filter: 'brightness(1.2)' }} onError={e => { e.target.style.display='none'; }} />
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '17px', fontWeight: '800', color: '#ffffff', letterSpacing: '0.8px' }}>HAZZA INSTITUTE OF TECHNOLOGY</div>
                  <div style={{ fontSize: '11px', color: '#93c5fd', marginTop: '2px' }}>Hostel Management System</div>
                </div>
              </div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#f5bc1f', letterSpacing: '1px', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '8px' }}>HOSTEL ADMISSION FORM</div>
              <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '4px' }}>Hostel: <strong style={{ color: '#fff' }}>{hostelName || record.hostel_name}</strong></div>
            </div>
            <div style={{ padding: '20px 24px' }}>
            {[
              ['Full Name', record.full_name], ['Father\'s Name', record.father_name], ['Gender', record.gender],
              ['Date of Birth', fmtDate(record.dob)], ['CNIC', record.cnic], ['Campus', record.campus_name], ['Course', record.course_name], ['Course Level', record.course_level_name ? `Level ${record.course_level_number} - ${record.course_level_name}` : '—'],
              ['Contact No.', record.contact_no], ['Email', record.email], ['Permanent Address', record.permanent_address],
              ['Guardian Name', record.guardian_name], ['Relationship', record.guardian_relationship], ['Emergency Contact', record.guardian_contact],
              ['Date of Admission', fmtDate(record.date_of_admission)], ['Date of Exit', fmtDate(record.date_of_exit)],
              ['Monthly Rent', record.monthly_rent ? `Rs. ${Number(record.monthly_rent).toLocaleString()}` : '—'],
              ['Status', record.status],
              ['Notes', record.notes]
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', borderBottom: '1px solid #e5e7eb', padding: '5px 0', fontSize: '12px' }}>
                <strong style={{ color: '#374151' }}>{l}</strong>
                <span>{v || '—'}</span>
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px' }}>
              <div style={{ textAlign: 'center' }}>
                {record.student_signature ? <img src={record.student_signature} alt="sig" style={{ maxHeight: '55px', border: '1px solid #d1d5db', marginBottom: '6px' }} /> : <div style={{ height: '55px', borderBottom: '1px solid #000', marginBottom: '6px' }}></div>}
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#374151' }}>Student Signature</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                {record.incharge_signature ? <img src={record.incharge_signature} alt="sig" style={{ maxHeight: '55px', border: '1px solid #d1d5db', marginBottom: '6px' }} /> : <div style={{ height: '55px', borderBottom: '1px solid #000', marginBottom: '6px' }}></div>}
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#374151' }}>In-charge Signature</div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '11px', color: '#374151' }}>Date: ____________________________</div>
            </div>{/* end padding div */}
          </div>
        </div>
      </div>
    </div>
  );
}

// Request Edit Modal
function RequestEditModal({ admission, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    full_name: admission.full_name || '', father_name: admission.father_name || '',
    gender: admission.gender || '', dob: admission.dob ? admission.dob.split('T')[0] : '',
    cnic: admission.cnic || '', dept_batch_program: admission.dept_batch_program || '',
    contact_no: admission.contact_no || '', email: admission.email || '',
    permanent_address: admission.permanent_address || '', guardian_name: admission.guardian_name || '',
    guardian_relationship: admission.guardian_relationship || '',
    guardian_contact: admission.guardian_contact || '', monthly_rent: admission.monthly_rent || '',
    notes: admission.notes || '', date_of_exit: admission.date_of_exit ? admission.date_of_exit.split('T')[0] : ''
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!reason) { setError('Please provide a reason for this request'); return; }
    setSaving(true);
    try {
      await submitHostelRequest({ admission_id: admission.id, request_type: 'edit', requested_data: form, reason });
      onSubmitted();
    } catch (e) { setError(e.response?.data?.error || 'Failed to submit request'); }
    setSaving(false);
  };

  const inputStyle = { width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '8px 12px', color: '#e2e8f0', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { color: '#94a3b8', fontSize: '12px', display: 'block', marginBottom: '4px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#0f1f30', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', maxWidth: '540px', width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 style={{ color: '#fff', margin: 0 }}>Request Edit</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '18px' }}><FiX /></button>
        </div>
        <div style={{ padding: '20px', display: 'grid', gap: '12px' }}>
          {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '8px 12px', color: '#fca5a5', fontSize: '13px' }}>{error}</div>}
          <p style={{ color: '#94a3b8', margin: 0, fontSize: '13px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '6px', padding: '10px' }}>
            ℹ️ This will submit a request to the admin. Your changes will only be applied after admin approval.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {[['full_name', 'Full Name *'], ['father_name', "Father's Name"], ['contact_no', 'Contact No.'], ['email', 'Email'],
              ['cnic', 'CNIC (13 digits)'], ['dept_batch_program', 'Dept / Batch / Program']].map(([key, lbl]) => (
              <div key={key}><label style={labelStyle}>{lbl}</label><input style={inputStyle} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} /></div>
            ))}
            <div>
              <label style={labelStyle}>Gender</label>
              <select style={{ ...inputStyle, background: '#1a2d3e' }} value={form.gender} onChange={e => setForm(p => ({ ...p, gender: e.target.value }))}>
                <option value="" style={{ background: '#1a2d3e' }}>-- Select --</option>
                <option value="Male" style={{ background: '#1a2d3e' }}>Male</option>
                <option value="Female" style={{ background: '#1a2d3e' }}>Female</option>
                <option value="Other" style={{ background: '#1a2d3e' }}>Other</option>
              </select>
            </div>
            <div><label style={labelStyle}>Date of Birth</label><input type="date" style={inputStyle} value={form.dob} onChange={e => setForm(p => ({ ...p, dob: e.target.value }))} /></div>
          </div>
          {[['permanent_address', 'Permanent Address'], ['guardian_name', 'Guardian Name'],
            ['guardian_relationship', 'Guardian Relationship'], ['guardian_contact', 'Emergency Contact']].map(([key, lbl]) => (
            <div key={key}><label style={labelStyle}>{lbl}</label><input style={inputStyle} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} /></div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label style={labelStyle}>Monthly Rent (Rs.)</label><input type="number" style={inputStyle} value={form.monthly_rent} onChange={e => setForm(p => ({ ...p, monthly_rent: e.target.value }))} /></div>
            <div><label style={labelStyle}>Date of Exit (if exiting)</label><input type="date" style={inputStyle} value={form.date_of_exit} onChange={e => setForm(p => ({ ...p, date_of_exit: e.target.value }))} /></div>
          </div>
          <div><label style={labelStyle}>Notes</label><input style={inputStyle} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          <div>
            <label style={{ ...labelStyle, color: '#f59e0b' }}>Reason for Edit Request *</label>
            <textarea style={{ ...inputStyle, minHeight: '70px' }} value={reason} onChange={e => setReason(e.target.value)} placeholder="Briefly explain why this change is needed..." />
          </div>
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '8px 16px', background: '#0e1e45', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
            {saving ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Direct Edit Modal (when hostel has perm_edit_approval = 0)
function DirectEditModal({ admission, onClose, onSaved }) {
  const [form, setForm] = useState({ ...admission });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateHostelAdmissionPortal(admission.id, form);
      onSaved();
    } catch (e) { setError(e.response?.data?.error || 'Failed to update'); }
    setSaving(false);
  };

  const inputStyle = { width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '8px 12px', color: '#e2e8f0', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { color: '#94a3b8', fontSize: '12px', display: 'block', marginBottom: '4px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#0f1f30', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', maxWidth: '540px', width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 style={{ color: '#fff', margin: 0 }}><FiEdit2 /> Edit Resident</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '18px' }}><FiX /></button>
        </div>
        <div style={{ padding: '20px', display: 'grid', gap: '12px' }}>
          {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '8px 12px', color: '#fca5a5', fontSize: '13px' }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {[['full_name', 'Full Name *'], ['father_name', "Father's Name"], ['contact_no', 'Contact No.'], ['email', 'Email'], ['cnic', 'CNIC'], ['dept_batch_program', 'Dept/Batch/Program']].map(([key, lbl]) => (
              <div key={key}><label style={labelStyle}>{lbl}</label><input style={inputStyle} value={form[key] || ''} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} /></div>
            ))}
          </div>
          {[['permanent_address', 'Permanent Address'], ['guardian_name', 'Guardian Name'], ['guardian_contact', 'Emergency Contact'], ['notes', 'Notes']].map(([key, lbl]) => (
            <div key={key}><label style={labelStyle}>{lbl}</label><input style={inputStyle} value={form[key] || ''} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} /></div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label style={labelStyle}>Date of Admission</label><input type="date" style={inputStyle} value={form.date_of_admission || ''} onChange={e => setForm(p => ({ ...p, date_of_admission: e.target.value }))} /></div>
            <div><label style={labelStyle}>Monthly Rent (Rs.)</label><input type="number" style={inputStyle} value={form.monthly_rent || ''} onChange={e => setForm(p => ({ ...p, monthly_rent: e.target.value }))} /></div>
          </div>
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', background: '#0e1e45', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function HostelAdmissionsPortal({ hostel }) {
  const navigate = useNavigate();
  const [admissions, setAdmissions] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [printRecord, setPrintRecord] = useState(null);
  const [profileRecord, setProfileRecord] = useState(null);
  const [editRequest, setEditRequest] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  // Direct action states (when hostel has direct permissions)
  const [checkoutTarget, setCheckoutTarget] = useState(null);
  const [checkoutDate, setCheckoutDate] = useState('');
  const [checkoutSaving, setCheckoutSaving] = useState(false);
  const [directEditTarget, setDirectEditTarget] = useState(null);
  const [directDeleteTarget, setDirectDeleteTarget] = useState(null);
  const [directDeleteSaving, setDirectDeleteSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const [res, optionsRes] = await Promise.all([
        getHostelAdmissionsPortal(params),
        getHostelAdmissionOptionsPortal()
      ]);
      setAdmissions(res.data);
      setCampuses(optionsRes.data.campuses || []);
      setCourses(optionsRes.data.courses || []);
      setLevels(optionsRes.data.levels || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const validErr = validateAdmission(form);
    if (validErr) { setError(validErr); return; }
    setSaving(true); setError('');
    try {
      try {
        await addHostelAdmissionPortal(form);
      } catch (e) {
        if (e.response?.data?.warning && window.confirm(e.response.data.error + '\n\nClick OK to admit them here anyway, or Cancel to stop.')) {
          await addHostelAdmissionPortal({ ...form, force: true });
        } else {
          throw e;
        }
      }
      setShowAdd(false); setForm(emptyForm);
      await load();
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to save. Please check all fields and try again.';
      setError(msg);
      // scroll error into view
      setTimeout(() => { const el = document.getElementById('portal-add-error'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
    }
    setSaving(false);
  };

  const openPrint = async (id) => {
    try {
      const res = await getHostelAdmissionPortal(id);
      setPrintRecord(res.data);
    } catch (e) { alert('Failed to load record'); }
  };

  const handleDeleteRequest = async () => {
    if (!deleteReason) { alert('Please provide a reason'); return; }
    setDeleteSaving(true);
    try {
      await submitHostelRequest({ admission_id: deleteRequest.id, request_type: 'delete', reason: deleteReason });
      setDeleteRequest(null); setDeleteReason('');
      alert('Delete request submitted. Awaiting admin approval.');
    } catch (e) { alert(e.response?.data?.error || 'Failed to submit request'); }
    setDeleteSaving(false);
  };

  const handleCheckout = async () => {
    if (!checkoutDate) { alert('Please select check-out date'); return; }
    setCheckoutSaving(true);
    try {
      if (hostel?.perm_checkout_approval) {
        // needs approval — submit as request
        await submitHostelRequest({
          admission_id: checkoutTarget.id,
          request_type: 'checkout',
          requested_data: { date_of_exit: checkoutDate },
          reason: 'Checkout request'
        });
        alert('Checkout request submitted. Awaiting admin approval.');
      } else {
        // direct checkout
        await checkoutHostelAdmissionPortal(checkoutTarget.id, { date_of_exit: checkoutDate });
      }
      setCheckoutTarget(null); setCheckoutDate('');
      load();
    } catch (e) { alert(e.response?.data?.error || 'Checkout failed'); }
    setCheckoutSaving(false);
  };

  const handleDirectDelete = async () => {
    setDirectDeleteSaving(true);
    try {
      await deleteHostelAdmissionPortal(directDeleteTarget.id);
      setDirectDeleteTarget(null); load();
    } catch (e) { alert(e.response?.data?.error || 'Delete failed'); }
    setDirectDeleteSaving(false);
  };

  const setF = (key, val) => setForm((prev) => {
    const next = { ...prev, [key]: val };
    if (key === 'course_id') {
      const hit = courses.find(c => String(c.id) === String(val));
      if (hit) next.dept_batch_program = hit.name;
    }
    return next;
  });

  // Leaving the CNIC field auto-fills identity fields from an existing
  // record for the same person elsewhere in the system - avoids re-typing
  // (and mis-typing) a name/DOB/contact that's already on file.
  const [cnicLookupNote, setCnicLookupNote] = useState('');
  const handleCnicBlur = async () => {
    const cnic = (form.cnic || '').trim();
    if (cnic.length < 13) { setCnicLookupNote(''); return; }
    try {
      const res = await lookupByCnicPortal(cnic);
      if (res.data.found) {
        const d = res.data.data;
        setForm(prev => ({
          ...prev,
          full_name: prev.full_name || d.full_name || '',
          father_name: prev.father_name || d.father_name || '',
          gender: prev.gender || d.gender || '',
          dob: prev.dob || (d.dob ? String(d.dob).slice(0, 10) : ''),
          contact_no: prev.contact_no || d.contact_no || '',
          email: prev.email || d.email || '',
          permanent_address: prev.permanent_address || d.permanent_address || '',
          guardian_name: prev.guardian_name || d.guardian_name || '',
          guardian_relationship: prev.guardian_relationship || d.guardian_relationship || '',
          guardian_contact: prev.guardian_contact || d.guardian_contact || '',
        }));
        setCnicLookupNote(`Found an existing record for this CNIC (${d.full_name}) - filled in below, edit anything that's changed.`);
      } else {
        setCnicLookupNote('');
      }
    } catch (e) { /* non-fatal */ }
  };

  const inputStyle = { width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '8px 12px', color: '#e2e8f0', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { color: '#94a3b8', fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '4px' };
  const sectionStyle = { color: '#f5bc1f', fontSize: '13px', fontWeight: '700', borderBottom: '1px solid rgba(96,165,250,0.2)', paddingBottom: '6px', marginBottom: '4px' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ color: '#fff', margin: 0, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiHome /> Residents</h2>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '13px' }}>Manage hostel admission records</p>
        </div>
        <button
          onClick={() => { setShowAdd(true); setForm(emptyForm); setError(''); setCnicLookupNote(''); }}
          style={{ padding: '8px 18px', background: '#0e1e45', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
        >+ Add Resident</button>
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {[['', 'All'], ['active', 'Active'], ['exited', 'Exited']].map(([val, lbl]) => (
          <button key={val} onClick={() => setStatusFilter(val)} style={{ padding: '5px 14px', border: '1px solid', borderRadius: '6px', cursor: 'pointer', background: statusFilter === val ? '#0e1e45' : 'transparent', borderColor: '#0e1e45', color: statusFilter === val ? '#fff' : '#f5bc1f', fontSize: '13px', fontWeight: '500' }}>{lbl}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}><div style={{ width: '28px', height: '28px', border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #f5bc1f', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div></div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {admissions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#475569', background: 'rgba(255,255,255,0.03)', borderRadius: '10px' }}>
              No residents found. Add your first resident.
            </div>
          ) : admissions.map(a => (
            <div key={a.id} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <h4 style={{ color: '#e2e8f0', margin: '0 0 4px', fontSize: '15px' }}>{a.full_name}</h4>
                  <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '12px', color: '#64748b' }}>
                    <span><FiCalendar /> {a.date_of_admission ? new Date(a.date_of_admission).toLocaleDateString('en-PK') : '—'}</span>
                    <span><FiDollarSign /> Rs. {Number(a.monthly_rent || 0).toLocaleString()}/mo</span>
                    <span title="Dues"><FiBarChart2 /> Due: <span style={{ color: '#f59e0b' }}>Rs. {Number(a.total_dues || 0).toLocaleString()}</span></span>
                    <span title="Balance"><FiActivity /> Bal: <span style={{ color: (a.balance || 0) > 0 ? '#ef4444' : '#10b981', fontWeight: '600' }}>Rs. {Number(a.balance || 0).toLocaleString()}</span></span>
                    <span style={{ color: a.status === 'active' ? '#34d399' : '#9ca3af' }}>● {a.status}</span>
                  </div>
                  {a.contact_no && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}><FiPhone /> {a.contact_no} {a.cnic && <>· <FiCreditCard /> {a.cnic}</>}</div>}
                  {a.notes && <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '3px', fontStyle: 'italic' }}><FiFileText /> {a.notes}</div>}
                  <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <span><FiPlus /> Added: {a.created_at ? new Date(a.created_at).toLocaleString('en-PK', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
                    {a.updated_at && a.updated_at !== a.created_at && <span style={{ color: '#d97706' }}><FiEdit2 /> Updated: {new Date(a.updated_at).toLocaleString('en-PK', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <button onClick={() => setProfileRecord(a)} style={{ padding: '5px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#e2e8f0', cursor: 'pointer', fontSize: '12px' }} title="Complete profile"><FiUser /> Profile</button>
                  <button onClick={() => openPrint(a.id)} style={{ padding: '5px 12px', background: 'rgba(30,74,138,0.3)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: '6px', color: '#f5bc1f', cursor: 'pointer', fontSize: '12px' }}>View</button>

                  {/* Edit */}
                  {!hostel?.admissions_edit_hidden && (
                    hostel?.perm_edit_approval ? (
                      <button onClick={() => setEditRequest(a)} style={{ padding: '5px 12px', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '6px', color: '#fcd34d', cursor: 'pointer', fontSize: '12px' }}><FiEdit2 /> Request Edit</button>
                    ) : (
                      <button onClick={() => setDirectEditTarget(a)} style={{ padding: '5px 12px', background: 'rgba(245,158,11,0.25)', border: '1px solid rgba(245,158,11,0.5)', borderRadius: '6px', color: '#fbbf24', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}><FiEdit2 /> Edit</button>
                    )
                  )}

                  {/* Attendance / meal history */}
                  <button onClick={() => navigate(`/hostel/attendance?admission_id=${a.id}`)} style={{ padding: '5px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#e2e8f0', cursor: 'pointer', fontSize: '12px' }} title="Attendance history"><FiCheckSquare /></button>
                  <button onClick={() => navigate(`/hostel/meals?admission_id=${a.id}`)} style={{ padding: '5px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#e2e8f0', cursor: 'pointer', fontSize: '12px' }} title="Meal history"><FiCoffee /></button>

                  {/* Checkout (only for active residents) */}
                  {a.status === 'active' && (
                    hostel?.perm_checkout_approval ? (
                      <button onClick={() => { setCheckoutTarget(a); setCheckoutDate(new Date().toISOString().split('T')[0]); }} style={{ padding: '5px 12px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '6px', color: '#34d399', cursor: 'pointer', fontSize: '12px' }}><FiLogOut /> Request Checkout</button>
                    ) : (
                      <button onClick={() => { setCheckoutTarget(a); setCheckoutDate(new Date().toISOString().split('T')[0]); }} style={{ padding: '5px 12px', background: 'rgba(16,185,129,0.25)', border: '1px solid rgba(16,185,129,0.5)', borderRadius: '6px', color: '#10b981', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}><FiLogOut /> Checkout</button>
                    )
                  )}

                  {/* Delete */}
                  {hostel?.perm_delete_approval ? (
                    <button onClick={() => { setDeleteRequest(a); setDeleteReason(''); }} style={{ padding: '5px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#f87171', cursor: 'pointer', fontSize: '12px' }}><FiTrash2 /> Request Delete</button>
                  ) : (
                    <button onClick={() => setDirectDeleteTarget(a)} style={{ padding: '5px 12px', background: 'rgba(239,68,68,0.3)', border: '1px solid rgba(239,68,68,0.6)', borderRadius: '6px', color: '#f87171', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}><FiTrash2 /> Delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#0d1f2d', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', maxWidth: '640px', width: '100%', marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ color: '#fff', margin: 0 }}>New Hostel Admission</h3>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '20px' }}><FiX /></button>
            </div>
            <div style={{ padding: '22px', display: 'grid', gap: '16px', maxHeight: '70vh', overflowY: 'auto' }}>
              {error && <div id="portal-add-error" style={{ background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.5)', borderRadius: '8px', padding: '12px 14px', color: '#fca5a5', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'flex-start', gap: '8px' }}><span style={{ fontSize: '16px', flexShrink: 0 }}><FiSlash /></span><span>{error}</span></div>}

              {/* Auto-filled hostel */}
              <div><label style={{ ...labelStyle, color: '#34d399' }}>Hostel (Auto-filled)</label>
                <input style={{ ...inputStyle, opacity: 0.6 }} value={hostel?.name || ''} readOnly /></div>

              <h4 style={sectionStyle}>Personal Information</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[['full_name', 'Full Name *', 'Enter resident full name'], ['father_name', "Father's Name", "Enter father's full name"], ['cnic', 'CNIC (13 digits)', 'Enter 13-digit CNIC e.g. 3520112345671'], ['contact_no', 'Contact No.', 'Enter mobile e.g. 03001234567']].map(([k, l, ph]) => (
                  <div key={k}>
                    <label style={labelStyle}>{l}</label>
                    <input style={inputStyle} value={form[k]} onChange={e => setF(k, e.target.value)} onBlur={k === 'cnic' ? handleCnicBlur : undefined} placeholder={ph} />
                    {k === 'cnic' && cnicLookupNote && <small style={{ color: '#34d399', display: 'block', marginTop: '4px' }}>{cnicLookupNote}</small>}
                  </div>
                ))}
                <div><label style={labelStyle}>Gender</label>
                  <select style={{ ...inputStyle, color: form.gender ? '#e2e8f0' : '#64748b', background: '#1a2d3e' }} value={form.gender} onChange={e => setF('gender', e.target.value)}>
                    <option value="" style={{ background: '#1a2d3e', color: '#94a3b8' }}>-- Select Gender --</option>
                    <option value="Male" style={{ background: '#1a2d3e', color: '#e2e8f0' }}>Male</option>
                    <option value="Female" style={{ background: '#1a2d3e', color: '#e2e8f0' }}>Female</option>
                    <option value="Other" style={{ background: '#1a2d3e', color: '#e2e8f0' }}>Other</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Date of Birth (min 10 yrs old)</label><input type="date" style={inputStyle} max={MAX_DOB} value={form.dob} onChange={e => setF('dob', e.target.value)} /></div>
              </div>
              <div><label style={labelStyle}>Email</label><input type="email" style={inputStyle} value={form.email} onChange={e => setF('email', e.target.value)} placeholder="Enter email address e.g. student@example.com" /></div>
              <div><label style={labelStyle}>Course *</label>
                <select style={{ ...inputStyle, background: '#1a2d3e' }} value={form.course_id} onChange={e => setF('course_id', e.target.value)}>
                  <option value="" style={{ background: '#1a2d3e' }}>-- Select Course --</option>
                  {courses.map(c => <option key={c.id} value={c.id} style={{ background: '#1a2d3e' }}>{c.name} ({c.code})</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Permanent Address</label><textarea style={{ ...inputStyle, minHeight: '60px' }} value={form.permanent_address} onChange={e => setF('permanent_address', e.target.value)} placeholder="Enter full permanent address" /></div>

              <h4 style={sectionStyle}>Guardian / Emergency</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[['guardian_name', 'Guardian Name', 'Enter guardian full name'], ['guardian_relationship', 'Relationship', 'e.g. Father, Brother, Uncle'], ['guardian_contact', 'Emergency Contact', 'Enter guardian mobile number']].map(([k, l, ph]) => (
                  <div key={k}><label style={labelStyle}>{l}</label><input style={inputStyle} value={form[k]} onChange={e => setF(k, e.target.value)} placeholder={ph} /></div>
                ))}
              </div>

              <h4 style={sectionStyle}>Hostel Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div><label style={labelStyle}>Campus *</label>
                  <select style={{ ...inputStyle, background: '#1a2d3e' }} value={form.campus_id} onChange={e => setF('campus_id', e.target.value)}>
                    <option value="" style={{ background: '#1a2d3e' }}>-- Select Campus --</option>
                    {campuses.map(c => <option key={c.id} value={c.id} style={{ background: '#1a2d3e' }}>{c.name} ({c.code})</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Course Level *</label>
                  <select style={{ ...inputStyle, background: '#1a2d3e' }} value={form.course_level} onChange={e => setF('course_level', e.target.value)}>
                    <option value="" style={{ background: '#1a2d3e' }}>-- Select Level --</option>
                    {levels.map(l => <option key={l.id} value={l.id} style={{ background: '#1a2d3e' }}>Level {l.level} - {l.name}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Date of Admission *</label><input type="date" style={inputStyle} value={form.date_of_admission} onChange={e => setF('date_of_admission', e.target.value)} /></div>
                <div><label style={labelStyle}>Monthly Rent (Rs.) *</label><input type="number" style={inputStyle} value={form.monthly_rent} onChange={e => setF('monthly_rent', e.target.value)} placeholder="Enter monthly rent amount" /></div>
              </div>
              <div><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, minHeight: '50px' }} value={form.notes} onChange={e => setF('notes', e.target.value)} placeholder="Any additional notes or remarks" /></div>

              <h4 style={sectionStyle}>Signatures</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <SignaturePad label="Student Signature" value={form.student_signature} onChange={v => setF('student_signature', v)} />
                <SignaturePad label="In-charge Signature" value={form.incharge_signature} onChange={v => setF('incharge_signature', v)} />
              </div>
            </div>
            <div style={{ padding: '16px 22px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowAdd(false)} style={{ padding: '8px 18px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px', color: '#9ca3af', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAdd} disabled={saving} style={{ padding: '8px 20px', background: '#0e1e45', border: 'none', borderRadius: '7px', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : 'Add Resident'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print View */}
      {printRecord && <PrintView record={printRecord} hostelName={hostel?.name} onClose={() => setPrintRecord(null)} />}

      {/* Complete Profile View */}
      {profileRecord && (
        <ResidentProfileModal
          admission={profileRecord}
          campuses={campuses}
          courses={courses}
          onClose={() => setProfileRecord(null)}
          fetchAttendance={(admissionId) => getAttendanceHistoryPortal({ admission_id: admissionId })}
          fetchMeals={(admissionId) => getMealsHistoryPortal({ admission_id: admissionId })}
          fetchPayments={(admissionId) => getHostelPaymentsPortal({ admission_id: admissionId })}
          fetchInvoices={(admissionId) => getInvoicesPortal({ admission_id: admissionId })}
          fetchInvoiceDetail={(id) => getInvoicePortal(id)}
          fetchAdjustments={(admissionId) => getBillingAdjustmentsPortal({ admission_id: admissionId })}
        />
      )}

      {/* Edit Request Modal */}
      {editRequest && (
        <RequestEditModal admission={editRequest} onClose={() => setEditRequest(null)} onSubmitted={() => { setEditRequest(null); alert('Edit request submitted. Awaiting admin approval.'); }} />
      )}

      {/* Delete Request Modal */}
      {deleteRequest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#0f1f30', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', maxWidth: '400px', width: '100%' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ color: '#fff', margin: 0 }}>Request Delete</h3>
            </div>
            <div style={{ padding: '20px', display: 'grid', gap: '12px' }}>
              <p style={{ color: '#94a3b8', margin: 0, fontSize: '13px' }}>
                This will request admin to delete <strong style={{ color: '#e2e8f0' }}>{deleteRequest.full_name}</strong>'s record.
              </p>
              <div>
                <label style={{ ...labelStyle, color: '#ef4444' }}>Reason for Delete Request *</label>
                <textarea style={{ ...inputStyle, minHeight: '80px' }} value={deleteReason} onChange={e => setDeleteReason(e.target.value)} placeholder="Explain why this record should be deleted..." />
              </div>
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setDeleteRequest(null)} style={{ padding: '7px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeleteRequest} disabled={deleteSaving} style={{ padding: '7px 16px', background: 'rgba(239,68,68,0.7)', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
                {deleteSaving ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {checkoutTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#0f1f30', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', maxWidth: '400px', width: '100%' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ color: '#fff', margin: 0 }}>{hostel?.perm_checkout_approval ? '<FiLogOut /> Request Checkout' : '<FiLogOut /> Checkout Resident'}</h3>
              <button onClick={() => setCheckoutTarget(null)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '18px' }}><FiX /></button>
            </div>
            <div style={{ padding: '20px', display: 'grid', gap: '12px' }}>
              {hostel?.perm_checkout_approval && (
                <p style={{ color: '#f59e0b', margin: 0, fontSize: '13px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '6px', padding: '10px' }}>
                  ℹ️ A checkout request will be submitted. The resident will be marked as exited after admin approval.
                </p>
              )}
              <p style={{ color: '#94a3b8', margin: 0, fontSize: '13px' }}>Resident: <strong style={{ color: '#e2e8f0' }}>{checkoutTarget.full_name}</strong></p>
              <div>
                <label style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Date of Exit *</label>
                <input type="date" value={checkoutDate} onChange={e => setCheckoutDate(e.target.value)}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '8px 12px', color: '#e2e8f0', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setCheckoutTarget(null)} style={{ padding: '7px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCheckout} disabled={checkoutSaving} style={{ padding: '7px 16px', background: '#0e1e45', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
                {checkoutSaving ? 'Processing...' : (hostel?.perm_checkout_approval ? 'Submit Request' : 'Confirm Checkout')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Delete Confirm */}
      {directDeleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#0f1f30', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', maxWidth: '380px', width: '100%' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ color: '#fff', margin: 0 }}><FiTrash2 /> Delete Resident</h3>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ color: '#94a3b8', margin: 0, fontSize: '13px' }}>
                Permanently delete <strong style={{ color: '#e2e8f0' }}>{directDeleteTarget.full_name}</strong>'s record? This cannot be undone.
              </p>
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setDirectDeleteTarget(null)} style={{ padding: '7px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDirectDelete} disabled={directDeleteSaving} style={{ padding: '7px 16px', background: 'rgba(239,68,68,0.7)', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
                {directDeleteSaving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Edit Modal */}
      {directEditTarget && (
        <DirectEditModal
          admission={directEditTarget}
          onClose={() => setDirectEditTarget(null)}
          onSaved={() => { setDirectEditTarget(null); load(); }}
        />
      )}
    </div>
  );
}
